import { Flex, Text, VStack } from '@chakra-ui/layout';
import { Check, X } from 'lucide-react';
import { Button, Tag } from '@chakra-ui/react';
import { djs } from '@duke-hq/djs';

import { SlideKind } from '@duke-hq/svc-api-db';
import { ResourceStatusBadge } from '@duke-hq/ui/components/ResourceStatusBadge/ResourceStatusBadge';
import { nth } from '@duke-hq/lib';
import { RouterOutputs } from '@duke-hq/svc-api/admin';

function AssessmentSummary({
  submission,
  revisionNumber,
  onQuestionClick,
  isQuestionNavigable,
  slides,
}: {
  submission: RouterOutputs['assessments']['getAllByAssessmentEnrolmentId'][number];
  revisionNumber: number;
  onQuestionClick?: (questionId: string) => void;
  isQuestionNavigable?: (questionId: string) => boolean;
  slides: RouterOutputs['unitAssessment']['getSlides']['slides'];
}) {
  const allQuestionSlides = slides?.filter((slide) => slide.kind !== SlideKind.CONTENT);

  return (
    <Flex p={4} flexDirection="column" key={submission.id}>
      <Flex gap={2}>
        <Text fontWeight="semibold" fontSize="md">
          {nth(revisionNumber)} Submission
        </Text>
        <ResourceStatusBadge status={submission.outcome} />
      </Flex>
      <Text fontSize="sm">Submitted on {djs(submission.submitted_at).format('DD MMMM YYYY')}</Text>
      <VStack gap={2} mt={3}>
        {allQuestionSlides.map((slide, slideIndex) => {
          const questionAnswerMapping: Record<
            string,
            (typeof submission.content)[number] | undefined
          > = {};

          if (slide.kind === SlideKind.QUESTION_GROUP) {
            slide.questions.forEach((question) => {
              questionAnswerMapping[question.id] = submission.content.find(
                (answer) => answer.questionId === question.id,
              );
            });
          } else if (
            slide.kind === SlideKind.OPEN_QUESTION ||
            slide.kind === SlideKind.FILE_UPLOAD_PROMPT ||
            slide.kind === SlideKind.FORM
          ) {
            questionAnswerMapping[slide.id] = submission.content.find(
              (answer) => answer.questionId === slide.id,
            );
          }

          const submissionQuestionIds = submission.content.map((answer) => answer.questionId);
          const answers = Object.values(questionAnswerMapping);
          const questionIds = Object.keys(questionAnswerMapping);

          const correctAnswerIndex = answers.findIndex((answer) => answer?.correct);
          const hasCorrectAnswer = correctAnswerIndex > -1;
          if (!answers?.length) {
            return null;
          }

          if (!questionIds?.some((questionId) => submissionQuestionIds.includes(questionId))) {
            return;
          }

          return (
            <Flex w="100%" gap={2} key={slide.id} alignItems="center">
              {questionIds.map((questionId, questionIndex) => {
                const answer = questionAnswerMapping[questionId];
                const isClickable =
                  onQuestionClick != null && (isQuestionNavigable?.(questionId) ?? true);

                return (
                  <AnswerTag
                    key={questionId}
                    onClick={() => onQuestionClick?.(questionId)}
                    isClickable={isClickable}
                    isCorrect={!!answer?.correct}
                    isDisabled={hasCorrectAnswer ? correctAnswerIndex < questionIndex : false}
                  >
                    {`Q${slideIndex + 1}${questionIndex ? '-' + questionIndex : ''}`}
                  </AnswerTag>
                );
              })}
              {answers?.length ? (
                <Tag
                  fontSize="xs"
                  borderRadius="full"
                  h="fit-content"
                  minW="fit-content"
                  fontWeight="bold"
                  color="white"
                  maxW="72px"
                  width="100%"
                  display="flex"
                  justifyContent="center"
                  backgroundColor={hasCorrectAnswer ? 'success' : 'danger'}
                >
                  {hasCorrectAnswer ? 'Correct' : 'Incorrect'}
                </Tag>
              ) : null}
            </Flex>
          );
        })}
      </VStack>
    </Flex>
  );
}

export default AssessmentSummary;

function AnswerTag({
  isCorrect,
  children,
  onClick,
  isClickable,
  isDisabled,
}: {
  isCorrect: boolean;
  children: React.ReactNode;
  onClick: () => void;
  isClickable: boolean;
  isDisabled?: boolean;
}) {
  return (
    <Button
      variant="unstyled"
      display="flex"
      w="100%"
      height="fit-content"
      textAlign="center"
      cursor={isClickable && !isDisabled ? 'pointer' : 'default'}
      color={isCorrect ? 'success' : 'danger'}
      rounded={8}
      p={1.5}
      isDisabled={isDisabled}
      fontSize="sm"
      fontWeight="semibold"
      justifyContent="center"
      alignItems="center"
      gap={2}
      _disabled={{
        backgroundColor: 'bg.muted',
        color: 'fg.inverse-muted',
        _hover: {
          backgroundColor: 'bg.muted',
        },
      }}
      onClick={isDisabled || !isClickable ? undefined : onClick}
      backgroundColor={isCorrect ? 'bg.success-emphasis' : 'bg.danger-emphasis'}
    >
      {children}
      {!isDisabled ? (
        <Flex
          rounded="full"
          justifyContent="center"
          alignItems="center"
          backgroundColor={isCorrect ? 'success' : 'danger'}
          p={1}
          h="fit-content"
        >
          {isCorrect ? (
            <Check color="white" size={8} strokeWidth={4} />
          ) : (
            <X color="white" size={8} strokeWidth={4} />
          )}
        </Flex>
      ) : null}
    </Button>
  );
}
